import {
  IInventoryCreateDropdownListProps,
  IInventoryProductListGeneralDetailsCreateProps,
} from "@/api_framework/api_modals/Inventory";
import { useInventoryCountryListAPI } from "@/app/hooks/api_hooks/Inventory/useInventoryCountryListAPI";
import { useInventoryProductCreateAPI } from "@/app/hooks/api_hooks/Inventory/useInventoryProductCreateAPI";
import { useInventoryProductDetailsAPI } from "@/app/hooks/api_hooks/Inventory/useInventoryProductDetailsAPI";
import { useInventoryProductUpdateAPI } from "@/app/hooks/api_hooks/Inventory/useInventoryProductUpdateAPI";
import { useProductParentTitleAPI } from "@/app/hooks/api_hooks/Inventory/useProductParentTitleAPI";
import { useCouponCategoryAPI } from "@/app/hooks/api_hooks/coupon/useCouponCategoryAPI";
import { useCouponSubCategoryAPI } from "@/app/hooks/api_hooks/coupon/useCouponSubCategoryAPI";
import useFormValidations from "@/app/hooks/useFormValidations";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { IInventoryState } from "@/app_redux/reducers/slices/auth/inventory_slice";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import { useInventoryProductCatalougeDropdownAPI } from "@/app/hooks/api_hooks/Inventory/useInventoryProductCatalougeDropdownAPI";
import { useInventoryProductCatalogueDropdownItemCreateAPI } from "@/app/hooks/api_hooks/Inventory/useInventoryProductCatalogueDropdownItemCreateAPI";
import { toast } from "react-hot-toast";
import { useInventoryActiveTaxListAPI } from "@/app/hooks/api_hooks/Inventory/useInventoryActiveTaxListAPI";
import { useFileToS3LinkGeneratorAPI } from "@/app/hooks/api_hooks/LabReport/useFileToS3LinkGeneratorAPI";
import { useProductSubDetailsAPI } from "@/app/hooks/api_hooks/Inventory/ProductSetup/useProductSubDetailsAPI";
import { useMedicineConsumptionUnitAPI } from "@/app/hooks/api_hooks/Inventory/ProductSetup/useMedicineConsumptionUnitAPI";
import { IInventoryBatchFormFields, Props } from "./types";

export const useGeneralDetailsForm = ({ formHook, onParentTitleIdChange }: Props) => {
  const { getRouteKey } = useRouter();
  const routeId = getRouteKey("INVENTORY", "id");
  const { routeState } = useRouteInfo(routeId);
  const { taxList, productSubDetails, medicineConsumptionUnit } = routeState.state as IInventoryState;
  const { forOnlyNumber, forAllAcceptWithOutStartWithDotAndSpace, forOnlyNumbersWithDecimal } = useFormValidations();
  const navigate = useNavigate();
  const [_selectedOption, setSelectedOption] = useState<any>(null);
  const [params, _setparams] = useSearchParams();
  const fetchCountryAPI = useInventoryCountryListAPI();
  const updateProduct = useInventoryProductUpdateAPI();
  const { execute: fetchParentTitleList } = useProductParentTitleAPI();
  const { execute: createProductList } = useInventoryProductCreateAPI();
  const { execute: productDetailsAPI, productDetails, setProductDetails } = useInventoryProductDetailsAPI();
  const { execute: fetchCategory, serviceCategory: couponCategory } = useCouponCategoryAPI();
  const { execute: fetchSubCategory, serviceSubCategory: couponSubCategory } = useCouponSubCategoryAPI();
  const { searchResults: brandItems, execute: fetchBrands } = useInventoryProductCatalougeDropdownAPI("BRAND");
  const couponCategoryItem = useSelectMenuReducer(couponCategory, "category_name", "id");
  const couponSubCategoryItem = useSelectMenuReducer(couponSubCategory, "subcategory_name", "subcategory_id");
  const brandItemsList = useSelectMenuReducer(brandItems, "brand_name", "brand_id");
  const { searchResults: animalItems, execute: fetchAnimals } = useInventoryProductCatalougeDropdownAPI("ANIMAL_TYPE");
  const animalItemsList = useSelectMenuReducer(animalItems, "animal_type", "animal_type_id");
  const { searchResults: discountBucketItems, execute: fetchDiscountBucket } =
    useInventoryProductCatalougeDropdownAPI("DISCOUNT_BUCKET");
  const discountItemsList = useSelectMenuReducer(discountBucketItems, "discount_bucket", "discount_bucket_id");
  const { execute: createDropdownItemsAPI } = useInventoryProductCatalogueDropdownItemCreateAPI();
  const taxListItems = useSelectMenuReducer(taxList, "hsn_code", "tax_id");
  const medicineConsumptionListItems = useSelectMenuReducer(
    medicineConsumptionUnit,
    "dose_unit_name",
    "dose_unit_name",
  );
  const [isOpen, setIsOpen] = useState(false);
  const [fields, setFields] = useState([{ id: Date.now(), file: null as File | null }]);
  const fileInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [lotFields, setLotFields] = useState([{ id: Date.now(), file: null as File | null }]);
  const fileInputRefsLot = useRef<(HTMLInputElement | null)[]>([]);
  const [selectedImage, setSelectedImage] = useState<File | string | null>(null);
  const fetchTaxList = useInventoryActiveTaxListAPI();
  const { execute: uploadToS3 } = useFileToS3LinkGeneratorAPI();
  const { execute: fetchTags } = useInventoryProductCatalougeDropdownAPI("TAGS");
  const { execute: fetchProductSubDetails } = useProductSubDetailsAPI();
  const [editableFields, setEditableFields] = useState<Record<string, boolean>>({});
  const [loader, setLoader] = useState(false);
  const formattedTags = (productDetails?.tags || []).map((tag) => ({
    label: tag,
    value: tag,
  }));
  const productId = params?.get("product_id");
  const isEditMode = !!productId;
  const fetchMedicineConsumptionUnit = useMedicineConsumptionUnitAPI();

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const file = event.target.files?.[0] || null;
    if (!file) return;
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error("Image size must be less than 5 MB");
      event.target.value = "";
      return;
    }
    if (!allowedTypes.includes(file?.type)) {
      toast.error("Image type must be jpeg, jpg, or png");
      event.target.value = "";
      return;
    }
    try {
      const response = await uploadToS3(file, null, "product_setup_upc");
      if (response?.status && response?.message) {
        formHook.setValue(`upcs.${index}.image_url`, response?.message, {
          shouldValidate: true,
          shouldDirty: true,
        });
      } else {
        toast.error("Failed to upload image. Please try again.");
      }
    } catch (error) {
      toast.error("Something went wrong while uploading image.");
    }
  };

  const handleUploadClick = (index: number) => {
    fileInputRefs.current[index]?.click();
  };

  const addField = () => {
    const lastIndex = fields.length - 1;
    const lastField = fields[lastIndex];
    const upcName = formHook.getValues(`upcs.${lastIndex}.upcname`);
    const uploadedUrl = formHook.getValues(`upcs.${lastIndex}.image_url` as any);
    if (!upcName || upcName.trim() === "") {
      toast.error("Please enter UPC before adding a new row");
      return;
    }
    if (!lastField?.file && !uploadedUrl) {
      toast.error("Please upload an image before adding a new row");
      return;
    }
    setFields([...fields, { id: Date.now(), file: null }]);
    const nextIndex = lastIndex + 1;
    formHook.setValue(`upcs.${nextIndex}.upcname`, "");
    formHook.setValue(`upcs.${nextIndex}.image_url` as any, null);
  };

  const deleteField = (index: number) => {
    if (fields?.length === 1) {
      const newFields = [...fields];
      newFields[0] = { id: Date.now(), file: null };
      setFields(newFields);
      formHook.setValue(`upcs.${index}.upcname`, "");
      formHook.setValue(`upcs.${index}.image_url` as any, null);
    } else {
      const newFields = fields.filter((_, i) => i !== index);
      setFields(newFields);
      const upcs = formHook.getValues("upcs") || [];
      const newUpcs = [...upcs];
      newUpcs.splice(index, 1);
      formHook.setValue("upcs", newUpcs as any);
    }
  };

  const handleLotFileChange = async (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const file = event.target.files?.[0] || null;
    if (!file) return;
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error("Image size must be less than 5 MB");
      event.target.value = "";
      return;
    }
    if (!allowedTypes.includes(file?.type)) {
      toast.error("Image type must be jpeg, jpg, or png");
      event.target.value = "";
      return;
    }
    try {
      const response = await uploadToS3(file, null, "product_setup_lot");
      if (response?.status && response?.message) {
        const newFields = [...lotFields];
        newFields[index].file = file;
        setLotFields(newFields);
        formHook.setValue(`lots.${index}.image_url`, response.message);
      } else {
        toast.error("Failed to upload image. Please try again.");
      }
    } catch (error) {
      toast.error("Something went wrong while uploading image.");
    }
  };

  const handleLotUploadClick = (index: number) => {
    fileInputRefsLot?.current[index]?.click();
  };

  const addLotField = () => {
    const lastIndex = lotFields?.length - 1;
    const lastField = lotFields[lastIndex];
    const lotNumber = formHook.getValues(`lots.${lastIndex}.lot_number`);
    const mrpValue = formHook.getValues(`lots.${lastIndex}.mrp`);
    const expiryDate = formHook.getValues(`lots.${lastIndex}.date`);
    const imageFile = lastField?.file || formHook.getValues(`lots.${lastIndex}.image_url`);
    const isAnyFieldFilled =
      (lotNumber && String(lotNumber).trim() !== "") ||
      (mrpValue && String(mrpValue).trim() !== "" && Number(mrpValue) > 0) ||
      (expiryDate && String(expiryDate).trim() !== "") ||
      imageFile;
    if (!isAnyFieldFilled) {
      return;
    }
    if (!lotNumber || String(lotNumber).trim() === "") {
      toast.error("Lot Number is required");
      return;
    }
    if (mrpValue === undefined || mrpValue === null || String(mrpValue).trim() === "" || Number(mrpValue) <= 0) {
      toast.error("MRP is required");
      return;
    }
    if (!expiryDate || String(expiryDate).trim() === "") {
      toast.error("Expiry Date is required");
      return;
    }
    if (!imageFile) {
      toast.error("Please upload an image");
      return;
    }
    const newField = { id: Date.now(), file: null };
    setLotFields((prev) => [...prev, newField]);
    const nextIndex = lastIndex + 1;
    formHook.setValue(`lots.${nextIndex}.lot_number`, "");
    formHook.setValue(`lots.${nextIndex}.mrp`, null as any);
    formHook.setValue(`lots.${nextIndex}.date`, "");
    formHook.setValue(`lots.${nextIndex}.image_url`, null);
  };

  const deleteLotField = (index: number) => {
    if (lotFields?.length === 1) {
      const newFields = [...lotFields];
      newFields[0] = { id: Date.now(), file: null };
      setLotFields(newFields);
      const newLots = [{ lot_number: "", mrp: null, date: "" }];
      formHook.setValue("lots", newLots as any);
    } else {
      const newFields = lotFields.filter((_, i) => i !== index);
      setLotFields(newFields);
      const lots = formHook.getValues("lots") || [];
      const newLots = [...lots];
      newLots.splice(index, 1);
      formHook.setValue("lots", newLots as any);
    }
  };

  useEffect(() => {
    fetchMedicineConsumptionUnit();
  }, []);

  useEffect(() => {
    if (productId) {
      productDetailsAPI(productId);
    }
    fetchCategory();
  }, [params]);

  useEffect(() => {
    setSelectedOption(formattedTags);
  }, [productDetails]);

  useEffect(() => {
    if (!productDetails) return;
    if (!productDetails?.general_details) return;
    formHook.setValue("name", productDetails?.variant_title);
    if (productDetails?.general_details?.upc_details?.length > 0) {
      const mappedUpcs = productDetails?.general_details?.upc_details?.map((upcItem: any) => ({
        upcname: upcItem?.upc ?? "",
        image_url: upcItem?.image_url,
      }));
      formHook.setValue("upcs", mappedUpcs);
      setFields(mappedUpcs?.map((_: any, i: number) => ({ id: Date.now() + i, file: null })));
    } else {
      formHook.setValue("upcs", [{ upcname: "", image_url: null }] as any);
      setFields([{ id: Date.now(), file: null }]);
    }
    formHook.setValue("vmscategory", productDetails?.general_details?.vms_category?.vms_category_name);
    formHook.setValue("vmscategoryId", productDetails?.general_details?.vms_category?.vms_category_id);
    formHook.setValue("vmsSubcategory", productDetails?.general_details?.vms_sub_category?.vms_sub_category_name);
    formHook.setValue("vmsSubcategoryId", productDetails?.general_details?.vms_sub_category?.vms_sub_category_id);
    formHook.setValue("productQuantity", productDetails?.general_details?.quantity);
    formHook.setValue("is_shrinkable", productDetails?.is_shrinkable);
    formHook.setValue("shrinkage", productDetails?.general_details?.is_shrinkable);
    formHook.setValue("salt_name", productDetails?.general_details?.salt_name);
    formHook.setValue("prescription_type", productDetails?.prescription_type ?? "");
    formHook.setValue("sku_name", productDetails?.general_details?.sku_name);
    formHook.setValue("billing_name", productDetails?.general_details?.billing_name);
    formHook.setValue("discount_bucket", productDetails?.general_details?.discount_bucket);
    formHook.setValue("comment", productDetails?.general_details?.comments);
    formHook.setValue("is_medicine", productDetails?.general_details?.is_medicine);
    formHook.setValue("logic_type", productDetails?.general_details?.medicine_consumption_logic_type);
    formHook.setValue("logic_formula", productDetails?.general_details?.medicine_consumption_logic_formula);
    formHook.setValue("unit", productDetails?.general_details?.medicine_consumption_unit);
    formHook.setValue("weight_size", productDetails?.general_details?.medicine_weight_size_per_qty);
    formHook.setValue("parent_name", productDetails?.general_details?.parent_name);
    const isParent = productDetails?.general_details?.parent_details?.have_parent;
    if (isParent) {
      formHook.setValue("isproductParent", "yes");
    } else {
      formHook.setValue("isproductParent", "no");
    }
    formHook.setValue("parentTitle", productDetails?.general_details?.parent_details?.parent_title?.parent_title_name);
    formHook.setValue("parentTitleId", productDetails?.general_details?.parent_details?.parent_title?.parent_title_id);
    formHook.setValue("reference_mrp", productDetails?.reference_mrp);
    formHook.setValue("animalType", productDetails?.animal_type);
    formHook.setValue("permissible_variance", productDetails?.permissible_variance);
    if (productDetails?.lot_details && productDetails?.lot_details?.length > 0) {
      const mappedLots = productDetails?.lot_details?.map((lot: any) => ({
        lot_number: lot?.lot_number || "",
        mrp: lot?.mrp ?? null,
        date: lot?.expiry_date || "",
        image_url: lot?.image_url || null,
      }));
      formHook.setValue("lots", mappedLots);
      // @ts-expect-error: lot
      setLotFields(mappedLots);
    }
    if (productDetails?.tags?.length > 0) {
      formHook.setValue("tags", productDetails?.tags);
    } else {
      formHook.setValue("tags", []);
    }
    formHook.setValue("bar_code", productDetails?.bar_code);
    formHook.setValue("hsncode", productDetails?.tax_details?.hsn_code);
    formHook.setValue("cgst", productDetails?.tax_details?.tax_rate?.cgst_percentage);
    formHook.setValue("sgst", productDetails?.tax_details?.tax_rate?.sgst_percentage);
    formHook.setValue("igst", productDetails?.tax_details?.tax_rate?.igst_percentage);
    formHook.setValue("ugst", productDetails?.tax_details?.tax_rate?.utgst_percentage);
    formHook.setValue("outward_hsncode", productDetails?.outward_tax_details?.hsn_code);
    formHook.setValue("outward_cgst", productDetails?.outward_tax_details?.tax_rate?.cgst_percentage);
    formHook.setValue("outward_sgst", productDetails?.outward_tax_details?.tax_rate?.sgst_percentage);
    formHook.setValue("outward_igst", productDetails?.outward_tax_details?.tax_rate?.igst_percentage);
    formHook.setValue("outward_ugst", productDetails?.outward_tax_details?.tax_rate?.utgst_percentage);
    formHook.setValue("status", productDetails?.tax_details?.vms_visibility);
    formHook.setValue("brand", productDetails?.brand_name || productDetails?.vms_product_catalogue?.brand?.brand_name);
    formHook.setValue("brandId", productDetails?.brand_id || productDetails?.vms_product_catalogue?.brand?.brand_id);
    formHook.setValue("is_available", productDetails?.is_available);
    formHook.setValue("is_pack", productDetails?.general_details?.is_pack ?? false);
    formHook.setValue("visibility", productDetails?.po_visibility);
    formHook.setValue("tams_margin", productDetails?.warehouse_margin);
    formHook.setValue("rtv", productDetails?.rtv_allowed);
    formHook.setValue("rtvDays", productDetails?.rtv_days);
    formHook.setValue("avg_mrp", productDetails?.vms_product_catalogue?.avg_mrp);
    formHook.setValue("is_pack_sell_eligible", productDetails?.is_pack_sell_eligible);
  }, [productDetails, formHook]);

  useEffect(() => {
    if (!productSubDetails) return;
    const fields = {
      vmscategory: productSubDetails?.general_details?.vms_category?.vms_category_name,
      vmscategoryId: productSubDetails?.general_details?.vms_category?.vms_category_id,
      vmsSubcategory: productSubDetails?.general_details?.vms_sub_category?.vms_sub_category_name,
      vmsSubcategoryId: productSubDetails?.general_details?.vms_sub_category?.vms_sub_category_id,
      tams_margin: productSubDetails?.warehouse_margin,
      rtv: productSubDetails?.rtv_allowed,
      rtvDays: productSubDetails?.rtv_days,
      brandId: productSubDetails?.brand_id,
      brand: productSubDetails?.brand_name,
      animalType: productSubDetails?.species,
      hsncode: productSubDetails?.tax_details_vms?.hsn_code,
      cgst: productSubDetails?.tax_details_vms?.tax_rate?.cgst_percentage,
      sgst: productSubDetails?.tax_details_vms?.tax_rate?.sgst_percentage,
      igst: productSubDetails?.tax_details_vms?.tax_rate?.igst_percentage,
      ugst: productSubDetails?.tax_details_vms?.tax_rate?.utgst_percentage,
      outward_hsncode: productSubDetails?.outward_tax_details_vms?.hsn_code,
      outward_cgst: productSubDetails?.outward_tax_details_vms?.tax_rate?.cgst_percentage,
      outward_sgst: productSubDetails?.outward_tax_details_vms?.tax_rate?.sgst_percentage,
      outward_igst: productSubDetails?.outward_tax_details_vms?.tax_rate?.igst_percentage,
      outward_ugst: productSubDetails?.outward_tax_details_vms?.tax_rate?.utgst_percentage,
    };
    (Object.entries(fields) as [keyof IInventoryBatchFormFields, any][]).forEach(([key, value]) => {
      formHook.setValue(key, value ?? "");
    });
    const editable = Object.fromEntries(
      Object.entries(fields).map(([key, value]) => [key, value == null || value === ""]),
    );
    setEditableFields(editable);
  }, [productSubDetails]);

  useEffect(() => {
    if (formHook.watch("isproductParent") === "no") {
      formHook.setValue("vmscategory", "");
      formHook.setValue("vmscategoryId", "");
      formHook.setValue("vmsSubcategory", "");
      formHook.setValue("vmsSubcategoryId", "");
      formHook.setValue("brand", "");
      formHook.setValue("brandId", "");
      formHook.setValue("cgst", null);
      formHook.setValue("sgst", null);
      formHook.setValue("igst", null);
      formHook.setValue("ugst", null);
      formHook.setValue("outward_cgst", null);
      formHook.setValue("outward_sgst", null);
      formHook.setValue("outward_igst", null);
      formHook.setValue("outward_ugst", null);
      formHook.setValue("tams_margin", null);
      formHook.setValue("rtv", "");
      formHook.setValue("rtvDays", null);
      formHook.setValue("animalType", "");
      formHook.setValue("hsncode", "");
      formHook.setValue("outward_hsncode", "");
      formHook.setValue("salt_name", "");
    }
  }, [formHook.watch("isproductParent")]);

  useEffect(() => {
    if (formHook.watch("isproductParent") === "yes") {
      if (formHook.watch("parentTitleId") === "") {
        formHook.setValue("vmscategory", "");
        formHook.setValue("vmscategoryId", "");
        formHook.setValue("vmsSubcategory", "");
        formHook.setValue("vmsSubcategoryId", "");
        formHook.setValue("brand", "");
        formHook.setValue("brandId", "");
        formHook.setValue("cgst", null);
        formHook.setValue("sgst", null);
        formHook.setValue("igst", null);
        formHook.setValue("ugst", null);
        formHook.setValue("outward_cgst", null);
        formHook.setValue("outward_sgst", null);
        formHook.setValue("outward_igst", null);
        formHook.setValue("outward_ugst", null);
        formHook.setValue("tams_margin", null);
        formHook.setValue("rtv", "");
        formHook.setValue("rtvDays", null);
        formHook.setValue("animalType", "");
        formHook.setValue("hsncode", "");
        formHook.setValue("outward_hsncode", "");
        return;
      }
      formHook.setValue("vmscategory", productDetails?.general_details?.vms_category?.vms_category_name);
      formHook.setValue("vmscategoryId", productDetails?.general_details?.vms_category?.vms_category_id);
      formHook.setValue("vmsSubcategory", productDetails?.general_details?.vms_sub_category?.vms_sub_category_name);
      formHook.setValue("vmsSubcategoryId", productDetails?.general_details?.vms_sub_category?.vms_sub_category_id);
      formHook.setValue(
        "brand",
        productDetails?.brand_name || productDetails?.vms_product_catalogue?.brand?.brand_name,
      );
      formHook.setValue("brandId", productDetails?.brand_id || productDetails?.vms_product_catalogue?.brand?.brand_id);
      formHook.setValue("hsncode", productDetails?.tax_details?.hsn_code);
      formHook.setValue("cgst", productDetails?.tax_details?.tax_rate?.cgst_percentage);
      formHook.setValue("sgst", productDetails?.tax_details?.tax_rate?.sgst_percentage);
      formHook.setValue("igst", productDetails?.tax_details?.tax_rate?.igst_percentage);
      formHook.setValue("ugst", productDetails?.tax_details?.tax_rate?.utgst_percentage);
      formHook.setValue("outward_hsncode", productDetails?.outward_tax_details?.hsn_code);
      formHook.setValue("outward_cgst", productDetails?.outward_tax_details?.tax_rate?.cgst_percentage);
      formHook.setValue("outward_sgst", productDetails?.outward_tax_details?.tax_rate?.sgst_percentage);
      formHook.setValue("outward_igst", productDetails?.outward_tax_details?.tax_rate?.igst_percentage);
      formHook.setValue("outward_ugst", productDetails?.outward_tax_details?.tax_rate?.utgst_percentage);
      formHook.setValue("tams_margin", productDetails?.warehouse_margin);
      formHook.setValue("rtv", productDetails?.rtv_allowed);
      formHook.setValue("rtvDays", productDetails?.rtv_days);
      formHook.setValue("animalType", productDetails?.animal_type);
    }
  }, [formHook.watch("isproductParent")]);

  useEffect(() => {
    fetchCountryAPI();
    fetchCategory();
    fetchBrands();
    fetchDiscountBucket();
    fetchTaxList();
    fetchAnimals();
    fetchTags();
    const formData = formHook.getValues();
    if (formData?.vmscategoryId && formData?.vmscategoryId !== "0") {
      fetchSubCategory(formData?.vmscategoryId);
    }
  }, [formHook.watch("vmscategoryId")]);

  useEffect(() => {
    const isProductParent = formHook.watch("isproductParent");
    const variantTitle = formHook.watch("name");
    const parentName = formHook.watch("parent_name");
    const parentTitle = formHook.watch("parentTitle");
    const salt = formHook.watch("salt_name");
    const isPack = formHook.watch("is_pack");

    let combined = "";

    if (isProductParent === "yes") {
      // YES case
      if (isPack) {
        combined = [salt, parentTitle].filter(Boolean).join(" ");
      } else {
        combined = variantTitle
          ? `${[salt, parentTitle].filter(Boolean).join(" ")} - ${variantTitle}`
          : [salt, parentTitle].filter(Boolean).join(" ");
      }
    } else {
      // NO case
      if (isPack) {
        combined = [parentName].filter(Boolean).join(" ");
      } else {
        combined = variantTitle
          ? `${[salt, parentName].filter(Boolean).join(" ")} - ${variantTitle}`
          : [parentName].filter(Boolean).join(" ");
      }
    }

    formHook.setValue("billing_name", combined);
    formHook.setValue("sku_name", combined);

    if (!isEditMode) {
      formHook.setValue("itemName", combined);
    }
  }, [
    formHook.watch("isproductParent"),
    formHook.watch("parent_name"),
    formHook.watch("parentTitle"),
    formHook.watch("salt_name"),
    formHook.watch("name"),
    formHook.watch("is_pack"),
  ]);

  const validateConditionalFormFields = (data: IInventoryBatchFormFields) => {
    let isValid = false;
    if (
      data?.name !== "" ||
      data.parentTitle !== "" ||
      data?.brand !== "" ||
      data?.comment !== "" ||
      data?.vmscategory !== "" ||
      data?.vmsSubcategory !== ""
    ) {
      isValid = true;
    }
    return isValid;
  };

  const taxFieldsBySection = {
    inward: ["cgst", "sgst", "igst", "ugst"],
    outward: ["outward_cgst", "outward_sgst", "outward_igst", "outward_ugst"],
  } as const;
  type TaxSection = keyof typeof taxFieldsBySection;

  const getTaxFields = (section: TaxSection) => taxFieldsBySection[section];
  const getTaxValue = (field: (typeof taxFieldsBySection)[TaxSection][number]) =>
    parseFloat(`${formHook.getValues(field)}`);

  const validateTaxPair = (section: TaxSection) => {
    const [cgst, sgst] = getTaxFields(section);
    const cgstValue = getTaxValue(cgst);
    const sgstValue = getTaxValue(sgst);
    if ([cgstValue, sgstValue].some(Number.isNaN)) return true;
    return cgstValue === sgstValue;
  };

  const validateTaxSum = (section: TaxSection, target: "igst" | "ugst") => {
    const [cgst, sgst, igst, ugst] = getTaxFields(section);
    const cgstValue = getTaxValue(cgst);
    const sgstValue = getTaxValue(sgst);
    const targetValue = getTaxValue(target === "igst" ? igst : ugst);
    if ([cgstValue, sgstValue, targetValue].some(Number.isNaN)) return true;
    return cgstValue + sgstValue === targetValue;
  };

  const triggerTaxValidation = (section: TaxSection) => {
    formHook.trigger([...getTaxFields(section)]);
  };

  const validateInwardTaxPair = () => validateTaxPair("inward");
  const validateOutwardTaxPair = () => validateTaxPair("outward");
  const triggerInwardTaxValidation = () => triggerTaxValidation("inward");
  const triggerOutwardTaxValidation = () => triggerTaxValidation("outward");

  const getTaxSectionErrorMessage = (section: TaxSection) => {
    const errors = formHook.formState.errors as Record<string, { type?: string } | undefined>;
    const [cgst, sgst, igst, ugst] = getTaxFields(section);

    if ([cgst, sgst].some((field) => errors?.[field]?.type === "taxPair")) {
      return "CGST and SGST must be same";
    }
    if ([igst, ugst].some((field) => errors?.[field]?.type === "taxSum")) {
      return "IGST and UTGST must equal CGST + SGST";
    }
    return "";
  };

  useEffect(() => {
    formHook.setValue("vmscategoryId", `${couponCategory?.length}`);
    formHook.setValue("vmsSubcategoryId", `${couponSubCategory?.length}`);
  }, []);

  const onSubmit = (data: IInventoryBatchFormFields) => {
    const isFormSubmissionValid = validateConditionalFormFields(data);
    if (!isFormSubmissionValid) {
      return;
    }
    const selectedSubcategory = couponSubCategory?.find((cat: any) => cat.subcategory_id === data.vmsSubcategoryId);
    const isMedicineCategory = selectedSubcategory?.is_medicine === true;
    const lotDetails = data?.lots
      ?.filter((lot: any) => lot?.lot_number?.trim())
      ?.map((lot: any) => ({
        lot_number: lot.lot_number,
        mrp: lot?.mrp ? parseInt(`${lot.mrp}`) : null,
        expiry_date: lot?.date || null,
        image_url: lot?.image_url || null,
      }));
    const upcDetails = data?.upcs
      ?.filter((upc: any) => upc?.upcname?.trim())
      ?.map((upc: any) => ({
        upc: upc.upcname,
        image_url: upc?.image_url || null,
      }));
    if (data && isFormSubmissionValid) {
      const constructedBody: IInventoryProductListGeneralDetailsCreateProps = {
        variant_title: data?.name,
        billing_name: data?.billing_name,
        sku_name: data?.sku_name,
        is_pack: !!data?.is_pack,
        quantity: parseInt(`${data?.productQuantity}`),
        is_shrinkable: data?.is_shrinkable ? true : false,
        allow_shrinkage_on_issuance: data?.shrinkage ? true : false,
        parent_details: {
          have_parent: data?.isproductParent === "yes",
          ...(data?.isproductParent === "yes"
            ? {
                parent_title: {
                  parent_title_id: data?.parentTitleId,
                  parent_title_name: data?.parentTitle,
                },
              }
            : {
                parent_title: null,
              }),
        },
        ...(data?.isproductParent === "no" && {
          parent_name: data?.parent_name?.trim(),
        }),
        vms_category: {
          vms_category_id: data?.vmscategoryId,
          vms_category_name: data?.vmscategory,
        },
        vms_sub_category: {
          vms_sub_category_id: data?.vmsSubcategory === "" ? null : data?.vmsSubcategoryId,
          vms_sub_category_name: data?.vmsSubcategory === "" ? null : data?.vmsSubcategory,
        },
        brand: {
          brand_name: data?.brand === "" ? "null" : data?.brand,
          brand_id: data?.brand === "" ? "null" : data?.brandId,
        },
        warehouse_margin: parseInt(`${data?.tams_margin}`),
        tags: data?.tags?.length === 0 ? null : data?.tags,
        rtv_allowed: data?.rtv || null,
        rtv_days: parseInt(`${data?.rtvDays}`),
        animal_type: data?.animalType,
        tax_details: {
          hsn_code: data?.hsncode,
          tax_rate: {
            cgst_percentage: parseFloat(`${data?.cgst}`),
            sgst_percentage: parseFloat(`${data?.sgst}`),
            igst_percentage: parseFloat(`${data?.igst}`),
            utgst_percentage: parseFloat(`${data?.ugst}`),
          },
          vms_visibility: data?.status ? true : false,
        },
        outward_tax_details: {
          hsn_code: data?.outward_hsncode,
          tax_rate: {
            cgst_percentage: parseFloat(`${data?.outward_cgst}`),
            sgst_percentage: parseFloat(`${data?.outward_sgst}`),
            igst_percentage: parseFloat(`${data?.outward_igst}`),
            utgst_percentage: parseFloat(`${data?.outward_ugst}`),
          },
        },
        discount_bucket: parseInt(`${data?.discount_bucket}`),
        lot_details: lotDetails?.length ? lotDetails : null,
        upc_details: upcDetails?.length ? upcDetails : null,
        comments: data?.comment || null,
        permissible_variance: parseInt(`${data?.permissible_variance}`),
        reference_mrp: parseInt(`${data?.reference_mrp}`),
        po_visibility: data?.visibility ? true : false,
      };
      if (isMedicineCategory) {
        Object.assign(constructedBody, {
          salt_name: data?.salt_name?.trim() || null,
          prescription_type: data?.prescription_type || null,
          medicine_consumption_logic_type: data?.logic_type || null,
          medicine_consumption_logic_formula: data?.logic_formula ? Number(`${data?.logic_formula}`) : null,
          medicine_consumption_unit: data?.unit || null,
          medicine_weight_size_per_qty: data?.weight_size ? parseInt(`${data?.weight_size}`) : null,
        });
      }
      if (productDetails) {
        const updateBody = {
          ...constructedBody,
          action: "UPDATE_GENERAL_DETAILS",
        };
        setLoader(true);
        updateProduct(updateBody, productDetails?.vetic_variant_id)
          .then(({ status }) => {
            setLoader(false);
            if (status) {
              navigate(`/app/inventory/product-list`);
              setProductDetails(null);
            }
          })
          .catch(() => {
            setLoader(false);
          });
      } else {
        setLoader(true);
        createProductList(constructedBody)
          .then(({ status }) => {
            setLoader(false);
            if (status) {
              navigate(`/app/inventory/product-list`);
            }
          })
          .catch(() => {
            setLoader(false);
          });
      }
    }
  };

  const customStyles = {
    placeholder: (provided) => ({
      ...provided,
      "@apply text-sm": "text-xs",
    }),
    singleValue: (provided) => ({
      ...provided,
      "@apply text-sm": "",
    }),
    multiValue: (provided) => ({
      ...provided,
      "@apply text-sm": "text-xs",
    }),
    input: (provided) => ({
      ...provided,
      "@apply text-sm": "",
    }),
  };

  useEffect(() => {
    if (productDetails?.tags) {
      const defaultOptions = productDetails?.tags?.map((tag) => ({
        label: tag,
        value: tag,
      }));
      setSelectedOption(defaultOptions);
    } else {
      const defaultOptions = [
        {
          label: "",
          value: "",
        },
      ];
      setSelectedOption(defaultOptions);
    }
  }, [productDetails]);

  const handelCreateItems = (item: string, CATALOGUE) => {
    let constructedBody: IInventoryCreateDropdownListProps = null;
    if (CATALOGUE === "DISCOUNT_BUCKET") {
      constructedBody = {
        discount_bucket: item,
      };
    }
    createDropdownItemsAPI(constructedBody, CATALOGUE).then(({ status }) => {
      if (status) {
        if (CATALOGUE === "DISCOUNT_BUCKET") {
          fetchDiscountBucket().then(() => {
            formHook.setValue("discount_bucket", item);
          });
        }
      }
    });
  };

  const handleParentTitleSelect = async (item: any) => {
    if (item) {
      formHook.setValue("parentTitle", item.title);
      formHook.setValue("parentTitleId", item.id);
      try {
        const response = await fetchProductSubDetails(
          item.id,
          productDetails?.vetic_variant_id ? productDetails.vetic_variant_id : "",
        );
        if (!response || response.status === false || !response.data) {
          formHook.setValue("parentTitle", "");
          formHook.setValue("parentTitleId", "");
          formHook.setValue("vmscategory", "");
          formHook.setValue("vmscategoryId", "");
          formHook.setValue("vmsSubcategory", "");
          formHook.setValue("vmsSubcategoryId", "");
          formHook.setValue("brand", "");
          formHook.setValue("brandId", "");
          formHook.setValue("animalType", "");
          formHook.setValue("hsncode", "");
          formHook.setValue("cgst", null);
          formHook.setValue("sgst", null);
          formHook.setValue("igst", null);
          formHook.setValue("ugst", null);
          formHook.setValue("outward_hsncode", "");
          formHook.setValue("outward_cgst", null);
          formHook.setValue("outward_sgst", null);
          formHook.setValue("outward_igst", null);
          formHook.setValue("outward_ugst", null);
          formHook.setValue("tams_margin", null);
          formHook.setValue("rtv", "");
          formHook.setValue("rtvDays", null);
          toast.error(response?.message || "Failed to fetch product details.");
          return;
        }
        const subDetails = response.data;
        formHook.setValue("vmscategory", subDetails?.general_details?.vms_category?.vms_category_name ?? "");
        formHook.setValue("vmscategoryId", subDetails?.general_details?.vms_category?.vms_category_id ?? "");
        formHook.setValue("vmsSubcategory", subDetails?.general_details?.vms_sub_category?.vms_sub_category_name ?? "");
        formHook.setValue("vmsSubcategoryId", subDetails?.general_details?.vms_sub_category?.vms_sub_category_id ?? "");
        formHook.setValue("brand", subDetails?.brand_name ?? "");
        formHook.setValue("brandId", subDetails?.brand_id ?? "");
        formHook.setValue("animalType", subDetails?.species ?? "");
        formHook.setValue("hsncode", subDetails?.tax_details_vms?.hsn_code ?? "");
        formHook.setValue("cgst", subDetails?.tax_details_vms?.tax_rate?.cgst_percentage ?? null);
        formHook.setValue("sgst", subDetails?.tax_details_vms?.tax_rate?.sgst_percentage ?? null);
        formHook.setValue("igst", subDetails?.tax_details_vms?.tax_rate?.igst_percentage ?? null);
        formHook.setValue("ugst", subDetails?.tax_details_vms?.tax_rate?.utgst_percentage ?? null);
        formHook.setValue("outward_hsncode", subDetails?.outward_tax_details_vms?.hsn_code ?? "");
        formHook.setValue("outward_cgst", subDetails?.outward_tax_details_vms?.tax_rate?.cgst_percentage ?? null);
        formHook.setValue("outward_sgst", subDetails?.outward_tax_details_vms?.tax_rate?.sgst_percentage ?? null);
        formHook.setValue("outward_igst", subDetails?.outward_tax_details_vms?.tax_rate?.igst_percentage ?? null);
        formHook.setValue("outward_ugst", subDetails?.outward_tax_details_vms?.tax_rate?.utgst_percentage ?? null);
        formHook.setValue("tams_margin", subDetails?.warehouse_margin ?? null);
        formHook.setValue("rtv", subDetails?.rtv_allowed ?? "");
        formHook.setValue("rtvDays", subDetails?.rtv_days ?? null);
        if (subDetails?.tags?.length > 0) {
          formHook.setValue("tags", subDetails?.tags);
        } else {
          formHook.setValue("tags", []);
        }
        onParentTitleIdChange(item.id);
      } catch (error) {
        formHook.setValue("parentTitle", "");
        formHook.setValue("parentTitleId", "");
        formHook.setValue("vmscategory", "");
        formHook.setValue("vmscategoryId", "");
        formHook.setValue("vmsSubcategory", "");
        formHook.setValue("vmsSubcategoryId", "");
        formHook.setValue("brand", "");
        formHook.setValue("brandId", "");
        formHook.setValue("animalType", "");
        formHook.setValue("hsncode", "");
        formHook.setValue("cgst", null);
        formHook.setValue("sgst", null);
        formHook.setValue("igst", null);
        formHook.setValue("ugst", null);
        formHook.setValue("outward_hsncode", "");
        formHook.setValue("outward_cgst", null);
        formHook.setValue("outward_sgst", null);
        formHook.setValue("outward_igst", null);
        formHook.setValue("outward_ugst", null);
        formHook.setValue("tams_margin", null);
        formHook.setValue("rtv", "");
        formHook.setValue("rtvDays", null);
        toast.error("Failed to fetch product sub-details. Please try again.");
      }
    } else {
      formHook.setValue("parentTitle", "");
      formHook.setValue("parentTitleId", "");
      formHook.setValue("vmscategory", "");
      formHook.setValue("vmscategoryId", "");
      formHook.setValue("vmsSubcategory", "");
      formHook.setValue("vmsSubcategoryId", "");
      formHook.setValue("brand", "");
      formHook.setValue("brandId", "");
      formHook.setValue("animalType", "");
      formHook.setValue("hsncode", "");
      formHook.setValue("cgst", null);
      formHook.setValue("sgst", null);
      formHook.setValue("igst", null);
      formHook.setValue("ugst", null);
      formHook.setValue("outward_hsncode", "");
      formHook.setValue("outward_cgst", null);
      formHook.setValue("outward_sgst", null);
      formHook.setValue("outward_igst", null);
      formHook.setValue("outward_ugst", null);
      formHook.setValue("tams_margin", null);
      formHook.setValue("rtv", "");
      formHook.setValue("rtvDays", null);
    }
  };

  useEffect(() => {
    const isProductParent = formHook.watch("isproductParent");
    if (isProductParent === "yes") {
      setEditableFields((prev) => ({
        ...prev,
        animalType: false,
        vmscategory: false,
        vmsSubcategory: false,
        brand: false,
        hsncode: true,
        cgst: true,
        sgst: true,
        igst: true,
        ugst: true,
        outward_hsncode: true,
        outward_cgst: true,
        outward_sgst: true,
        outward_igst: true,
        outward_ugst: true,
      }));
    } else {
      setEditableFields((prev) => ({
        ...prev,
        animalType: true,
        vmscategory: true,
        vmsSubcategory: true,
        brand: true,
        hsncode: true,
        cgst: true,
        sgst: true,
        igst: true,
        ugst: true,
        outward_hsncode: true,
        outward_cgst: true,
        outward_sgst: true,
        outward_igst: true,
        outward_ugst: true,
      }));
    }
  }, [formHook.watch("isproductParent")]);

  const clearMedicineFields = () => {
    formHook.setValue("prescription_type", "");
    formHook.setValue("logic_type", "");
    formHook.setValue("logic_formula", null as any);
    formHook.setValue("unit", "");
    formHook.setValue("weight_size", null as any);
  };

  const shouldEnableParentLinkedFields = isEditMode || formHook.watch("isproductParent") === "yes";

  return {
    forOnlyNumber,
    forAllAcceptWithOutStartWithDotAndSpace,
    forOnlyNumbersWithDecimal,
    fetchParentTitleList,
    productDetails,
    couponCategory,
    couponSubCategory,
    couponCategoryItem,
    couponSubCategoryItem,
    brandItemsList,
    animalItemsList,
    discountItemsList,
    taxListItems,
    medicineConsumptionListItems,
    isOpen,
    setIsOpen,
    fields,
    fileInputRefs,
    lotFields,
    fileInputRefsLot,
    selectedImage,
    setSelectedImage,
    editableFields,
    loader,
    formattedTags,
    isEditMode,
    handleFileChange,
    handleUploadClick,
    addField,
    deleteField,
    handleLotFileChange,
    handleLotUploadClick,
    addLotField,
    deleteLotField,
    onSubmit,
    validateInwardTaxPair,
    validateOutwardTaxPair,
    triggerInwardTaxValidation,
    triggerOutwardTaxValidation,
    validateTaxSum,
    getTaxSectionErrorMessage,
    customStyles,
    handelCreateItems,
    handleParentTitleSelect,
    clearMedicineFields,
    shouldEnableParentLinkedFields,
  };
};
